/* eslint-disable no-useless-escape */
import {useEffect, useState} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import './DocExplorerFilterAccordion.scss';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as ChevronIcon} from '../../../../assets/images/chevron-base-icon.svg';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {DocMetadataFilter} from '../../../../shared/interfaces';
import ArrFilterChip from '../../../common/arr-filter-chip/ArrFilterChip';
import ArrButton from '../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import FilterSelectionModal, {
  FILTER_SELECTION_MAP,
} from '../filter-selection-modal/FilterSelectionModal';
import _ from 'lodash';
import ArrSelectionChip, {
  ArrSelectionChipData,
} from '../../../common/arr-selection-chip/ArrSelectionChip';

export type filterSelectionType =
  | 'include'
  | 'exclude'
  | 'before'
  | 'after'
  | 'on'
  | 'between'
  | 'IN'
  | 'NOTIN';

type DocExplorerFilterAccordionProps = {
  filters: DocMetadataFilter[];
  title: string;
  expandText?: string;
  collapseText?: string;
  tooltipText?: string;
  tooltipTitle?: string;
  expand?: boolean;
  onFilterApply: (param: ActiveFilter[]) => void;
};
type FilterValue = {
  disabled: boolean;
  group: string | null;
  selected: boolean;
  text: string;
  value: string;
};
export type ActiveFilter = {
  id: string;
  dataType: 'NVARCHAR' | 'DATETIME';
  label: string;
  values?: FilterValue[];
  selectionType?: filterSelectionType;
  seperator?: string;
  startDate?: string;
  endDate?: string;
  isSelected?: boolean;
};

const DocExplorerFilterAccordion: React.FC<DocExplorerFilterAccordionProps> = ({
  filters,
  title,
  tooltipTitle = '',
  expandText = '',
  collapseText = '',
  tooltipText = '',
  expand = true,
  onFilterApply,
}) => {
  const [expanded, setExpanded] = useState(expand);

  const [parentFilters, setParentFilters] = useState<ArrSelectionChipData[]>(
    []
  );
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [openDropdownFocusEl, setOpenDropdownFocusEl] =
    useState<HTMLElement | null>(null);

  const [filterModalState, setFilterModalState] = useState<{
    anchorEl: HTMLElement | null;
    selectedFilter: ActiveFilter | null;
  }>({anchorEl: null, selectedFilter: null});

  useEffect(() => {
    function moveFocusOnCloseBtn(e: KeyboardEvent) {
      if (
        e.key === 'Tab' &&
        filterModalState.anchorEl &&
        !openDropdownFocusEl
      ) {
        e.preventDefault();
        (
          document.querySelector(
            '[data-testid="filter-selection-close-button"]'
          ) as HTMLElement
        )?.focus();
        setOpenDropdownFocusEl(filterModalState.anchorEl);
      }
    }

    window.addEventListener('keydown', moveFocusOnCloseBtn);
    return () => window.removeEventListener('keydown', moveFocusOnCloseBtn);
  }, [filterModalState.anchorEl, openDropdownFocusEl]);

  const getFilterInfo = (
    id: string,
    filterType: 'parent' | 'active'
  ): ActiveFilter => {
    if (filterType == 'parent') {
      const parentFilter =
        parentFilters.find((filter) => filter.id == id) ??
        ({} as ArrSelectionChipData);

      return {
        id: parentFilter.id,
        label: parentFilter.label,
        type: parentFilter.type,
        isSelected: parentFilter.isSelected,
      } as ActiveFilter;
    }
    return _.cloneDeep(
      activeFilters.find((filter) => filter.id == id) ?? ({} as ActiveFilter)
    );
  };
  const updateParentFilter = (id: string, isSelected: boolean) => {
    setParentFilters((prev) => {
      return prev.map((filter) => {
        if (filter.id == id) {
          return {...filter, isSelected: isSelected};
        }
        return filter;
      });
    });
  };
  const getActiveFiltersChip = () => {
    return activeFilters.map((filter) => {
      if (filter.type == 'NVARCHAR') {
        return (
          <ArrFilterChip
            key={filter.id}
            classNames="active-filter"
            filterName={[
              filter.label,
              FILTER_SELECTION_MAP[filter.selectionType ?? 'include'],
            ]}
            selections={
              filter.values?.map((value) => {
                return {id: value.text, value: `\"${value.text}\"`};
              }) ?? []
            }
            variant="v2"
            isResponsive={false}
            clickable
            id={filter.id}
            onClick={activeFilterClickHandler}
            onRemove={clearActiveFilterHandler}
            dataTestId={filter.label?.replaceAll(' ', '').toLowerCase()}
          />
        );
      } else if (filter.type == 'DATETIME') {
        let dateSelection = [
          {id: filter.startDate ?? '', value: filter.startDate ?? ''},
        ];
        if (filter.endDate) {
          dateSelection = [
            ...dateSelection,
            {id: filter.endDate ?? '', value: filter.endDate ?? ''},
          ];
        }
        return (
          <ArrFilterChip
            key={filter.id}
            classNames="active-filter"
            filterName={[
              filter.label,
              FILTER_SELECTION_MAP[filter.selectionType ?? ''],
            ]}
            selections={[...dateSelection]}
            variant="v2"
            isResponsive={false}
            operator="to"
            clickable
            id={filter.id}
            onClick={activeFilterClickHandler}
            onRemove={clearActiveFilterHandler}
            dataTestId={filter.label?.replaceAll(' ', '').toLowerCase()}
          />
        );
      }
    });
  };

  const parentFilterClickHandler = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    filterData: ArrSelectionChipData
  ) => {
    let targetElement: HTMLElement | null = null;

    if ('currentTarget' in event) {
      targetElement = event.currentTarget;
    }

    if (
      event.type === 'click' ||
      ('key' in event && (event.key === 'Enter' || event.code === 'Space'))
    ) {
      event.preventDefault();
      if (filterModalState.selectedFilter?.id == filterData.id) return;
      const flterInfo: ActiveFilter = getFilterInfo(filterData.id, 'parent');
      if (flterInfo.isSelected) return;

      setFilterModalState({
        anchorEl: targetElement,
        selectedFilter: {...flterInfo},
      });
    }
  };
  const activeFilterClickHandler = (
    element: HTMLElement | null,
    id?: string
  ) => {
    if (element) {
      setFilterModalState({
        anchorEl: element,
        selectedFilter: {...getFilterInfo(id ?? '', 'active')},
      });
    }
  };
  const modalCloseHandler = () => {
    setFilterModalState({
      anchorEl: null,
      selectedFilter: null,
    });
    setTimeout(() => {
      openDropdownFocusEl?.focus();
    }, 100);
    setOpenDropdownFocusEl(null);
  };
  const onAddToFiltersHandler = (filterData: ActiveFilter) => {
    const existingActiveFilter = activeFilters.find(
      (filter) => filter.id == filterData.id
    );
    let updatedActiveFilters = activeFilters.map((filter) => {
      if (filter.id == filterData.id) return _.cloneDeep(filterData);
      return filter;
    });
    if (!existingActiveFilter) {
      updatedActiveFilters = [...updatedActiveFilters, _.cloneDeep(filterData)];
    }
    setActiveFilters([...updatedActiveFilters]);
    updateParentFilter(filterData.id, true);
    modalCloseHandler();
  };
  const clearAllHandler = () => {
    modalCloseHandler();
    setActiveFilters([]);
    setParentFilters((prev) => {
      return prev.map((filter) => {
        return {...filter, isSelected: false};
      });
    });
    onFilterApply([]);
  };
  const clearActiveFilterHandler = (id?: string) => {
    if (id == filterModalState.selectedFilter?.id) {
      modalCloseHandler();
    }
    setActiveFilters((prev) => {
      const filterData = prev.filter((filter) => filter.id != id);
      if (!filterData.length) onFilterApply([]);
      return filterData;
    });
    setParentFilters((prev) => {
      return prev.map((filter) => {
        if (filter.id == id) {
          return {...filter, isSelected: false};
        }
        return {...filter};
      });
    });
  };

  useEffect(() => {
    const mapedParentFilters = filters.map((data) => {
      return {
        id: data.projectFieldId,
        label: data.projectFieldLabel,
        type: data.dataType,
        isSelected: false,
      } as ArrSelectionChipData;
    });
    setParentFilters(mapedParentFilters);
  }, [filters]);

  const iconComponent = (
    <Box>
      <ChevronIcon className="expand-icon" />
    </Box>
  );
  return (
    <Box
      className="doc-explorer-filter-accordion"
      data-test-id={'DocExplorerFilterAccordion'}
    >
      <Accordion
        expanded={expanded}
        onChange={() =>
          setExpanded((prev) => {
            modalCloseHandler();
            return !prev;
          })
        }
      >
        <AccordionSummary expandIcon={iconComponent}>
          <Box className="accordion-summary-content">
            <Box className="title-section">
              <Typography
                className="typo-text subhead4_size title"
                data-testid={`doc-explorer-filter-accordion-title`}
                tabIndex={0}
              >
                {title}
              </Typography>
              <ArrTooltipWrapper
                title={tooltipTitle}
                closeTooltipOnEscPress
                trigger={
                  <IconButton
                    aria-label="Info"
                    className="info-icon-button"
                    tabIndex={-1}
                  >
                    <InfoIcon className="info-icon" tabIndex={0} />
                  </IconButton>
                }
              >
                {tooltipText}
              </ArrTooltipWrapper>
            </Box>

            <Box className="expand-icon-text-section">
              <Typography
                className=" typo-text buttonSmall_size  title"
                data-testid={`doc-explorer-filter-accordion-expand/collapse-text`}
                tabIndex={0}
              >
                {expanded ? collapseText : expandText}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box className="accordion-body">
            <Box className="parent-filter-container">
              {parentFilters.map((data) => (
                <ArrSelectionChip
                  key={data.id}
                  filterData={data}
                  tabIndex={-1}
                  onClick={parentFilterClickHandler}
                  variant="v2"
                  dataTestId={data.label?.replaceAll(' ', '').toLowerCase()}
                />
              ))}
            </Box>
            {activeFilters.length != 0 && (
              <Box className="sub-filter-container">
                <Divider className="filter-divider" />
                <Box className="active-filter-section">
                  <Box className="active-filter-header-section">
                    <Typography
                      className="typo-text subhead4_size title"
                      data-testid={`doc-explorer-filter-accordion-active-filters`}
                    >
                      {t('docExplorer.filterAccordion.activeFilters')}
                    </Typography>
                    <Box className="active-filter-section-button">
                      <ArrButton
                        dataTestId="clearAllHandlerButton"
                        aria-label={t('common.button.clearAll')}
                        size="small"
                        variant="text"
                        onClick={clearAllHandler}
                      >
                        {t('common.button.clearAll')}
                      </ArrButton>
                      <ArrButton
                        dataTestId="applyFiltersHandlerButton"
                        aria-label={t(
                          'docExplorer.filterAccordion.applyFilters'
                        )}
                        onClick={() => {
                          onFilterApply(activeFilters);
                        }}
                        size="small"
                      >
                        {t('docExplorer.filterAccordion.applyFilters')}
                      </ArrButton>
                    </Box>
                  </Box>
                  <Box className="active-filter-filter-section">
                    {getActiveFiltersChip()}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      <FilterSelectionModal
        anchorElement={filterModalState.anchorEl}
        onClose={modalCloseHandler}
        filterSelectionData={
          filterModalState.selectedFilter ?? ({} as ActiveFilter)
        }
        onAddToFilters={onAddToFiltersHandler}
        closeOnEsc
      />
    </Box>
  );
};

export default DocExplorerFilterAccordion;
